import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, webAuthnAPI, notificationsAPI } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import DeleteButton from '../components/ui/DeleteButton';
import { Camera, LogOut, FileText, User, ChevronRight, Fingerprint, Trash2, Plus, Bell, Sun, Moon, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import { getPermissionLabel } from '../utils/permissions';
import { registerPush } from '../utils/push';
import { useTheme } from '../contexts/ThemeContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { startRegistration } from '@simplewebauthn/browser';
import { useModalHistory } from '../hooks/useModalHistory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileModal = useModalHistory(isProfileOpen, setIsProfileOpen);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  // Biometric state
  const [credentials, setCredentials] = useState([]);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [isBiometricOpen, setIsBiometricOpen] = useState(false);
  const biometricModal = useModalHistory(isBiometricOpen, setIsBiometricOpen);
  const [deviceName, setDeviceName] = useState('');

  useEffect(() => {
    setBiometricSupported(
      typeof window !== 'undefined' &&
      window.PublicKeyCredential !== undefined
    );
  }, []);

  const [testingPush, setTestingPush] = useState(false);

  const handleTestPush = async () => {
    setTestingPush(true);
    try {
      // Re-register push token first (ensures token is fresh with correct VAPID key)
      const regResult = await registerPush();

      if (regResult === 'ios-not-installed') {
        toast.warning(
          'On iPhone, push notifications require the app to be installed. Tap Share → "Add to Home Screen" in Safari, then open the app from your Home Screen and try again.',
          { duration: 8000 }
        );
        return;
      }
      if (regResult === 'not-supported') {
        toast.warning(
          'Push notifications are not supported on this browser. iPhone users need iOS 16.4 or later with the app installed from the Home Screen.',
          { duration: 8000 }
        );
        return;
      }
      if (regResult === 'permission-denied') {
        toast.warning(
          'Notification permission was denied. Go to your device Settings → [App/Safari] → Notifications and allow them, then try again.',
          { duration: 8000 }
        );
        return;
      }
      if (!regResult || regResult === 'no-token') {
        toast.warning('Could not register for push notifications. Please try again or check your browser settings.');
        return;
      }

      const res = await notificationsAPI.testPush();
      const data = res.data;
      if (data.sent > 0) {
        toast.success('Test notification sent! Check your device.');
      } else {
        toast.warning(data.message || 'No push token found. Allow notifications and try again.');
      }
    } catch {
      toast.error('Failed to send test notification');
    } finally {
      setTestingPush(false);
    }
  };


  const loadCredentials = useCallback(async () => {
    try {
      const res = await webAuthnAPI.listCredentials();
      setCredentials(res.data);
    } catch {
      setCredentials([]);
    }
  }, []);

  useEffect(() => {
    if (isBiometricOpen) loadCredentials();
  }, [isBiometricOpen, loadCredentials]);

  // Load on mount so the badge shows without opening the dialog
  useEffect(() => {
    if (biometricSupported) loadCredentials();
  }, [biometricSupported, loadCredentials]);

  const getPhotoUrl = (picture) => {
    if (!picture) return null;
    if (picture.startsWith('data:')) return picture;           // base64 data URL
    if (picture.startsWith('/api/uploads/')) return `${BACKEND_URL}${picture}`;  // legacy local path
    return picture;
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await authAPI.uploadPhoto(file);
      setUser((prev) => ({ ...prev, picture: res.data.picture }));
      toast.success('Photo updated');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authAPI.updateProfile(formData);
      setUser(res.data);
      toast.success('Profile updated');
      profileModal.close();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const { themeMode, setThemeMode } = useTheme();

  const themeOptions = [
    { mode: 'light', icon: <Sun className="w-3.5 h-3.5" />, label: 'Light' },
    { mode: 'dark',  icon: <Moon className="w-3.5 h-3.5" />, label: 'Dark' },
    { mode: 'auto',  icon: <Monitor className="w-3.5 h-3.5" />, label: 'Auto' },
  ];

  const handleSignOut = async () => {
    await logout();
    navigate('/login');
  };

  const handleEnrollBiometric = async () => {
    setEnrolling(true);
    try {
      const beginRes = await webAuthnAPI.registerBegin();
      const { challenge_id, options } = beginRes.data;
      const credential = await startRegistration({ optionsJSON: options });
      const name = deviceName.trim() || detectDeviceName();
      await webAuthnAPI.registerComplete(challenge_id, credential, name);
      toast.success(`Biometric login enabled for "${name}"`);
      setDeviceName('');
      loadCredentials();
    } catch (err) {
      if (err.name === 'InvalidStateError') {
        toast.error('This device is already registered.');
      } else if (err.name === 'NotAllowedError') {
        toast.error('Biometric registration was cancelled.');
      } else {
        toast.error(err.response?.data?.detail || 'Registration failed.');
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleRemoveCredential = async (cred_id, name) => {
    try {
      await webAuthnAPI.deleteCredential(cred_id);
      toast.success(`Removed "${name}"`);
      loadCredentials();
    } catch {
      toast.error('Failed to remove credential.');
    }
  };

  const detectDeviceName = () => {
    const ua = navigator.userAgent;
    if (/iPad/.test(ua)) return 'iPad';
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/Android/.test(ua)) return 'Android Device';
    if (/Macintosh/.test(ua)) return 'Mac';
    if (/Windows/.test(ua)) return 'Windows PC';
    return 'My Device';
  };

  const openProfile = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    });
    profileModal.open();
  };

  const photoUrl = getPhotoUrl(user?.picture);

  return (
    <div className="bg-slate-50 pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-5 sm:py-8">
        <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-1">Settings</h1>
        <p className="text-sm sm:text-base text-slate-600 mb-6 sm:mb-8">Manage your account</p>

        {/* Profile Card */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 mb-4 shadow-sm">
          <div className="flex items-center gap-4">
            {/* Avatar with upload */}
            <div className="relative flex-shrink-0">
              <button
                onClick={handlePhotoClick}
                data-testid="profile-photo-btn"
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-100 flex items-center justify-center overflow-hidden border-2 border-slate-200 hover:border-green-400 transition-colors"
                disabled={uploading}
              >
                {photoUrl && !photoUrl.includes('placeholder') ? (
                  <img src={photoUrl} alt={user?.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-8 h-8 sm:w-10 sm:h-10 text-green-400" />
                )}
              </button>
              <div className="absolute -bottom-0.5 -right-0.5 w-6 h-6 bg-green-700 rounded-full flex items-center justify-center border-2 border-white">
                <Camera className="w-3 h-3 text-white" />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
                data-testid="photo-file-input"
              />
              {uploading && (
                <div className="absolute inset-0 rounded-full bg-black/30 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            {/* Info */}
            <div className="min-w-0 flex-1">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900 truncate">{user?.name}</h2>
              <p className="text-xs sm:text-sm text-slate-500 truncate">{user?.email}</p>
              {user?.phone && <p className="text-xs sm:text-sm text-slate-500 truncate">{user.phone}</p>}
              <span className="inline-block mt-1 px-2 py-0.5 text-[10px] sm:text-xs font-medium bg-green-50 text-green-700 rounded-full">{getPermissionLabel(user?.permission_level)}</span>
              {credentials.length > 0 && (
                <span
                  data-testid="biometric-enabled-badge"
                  className="inline-flex items-center gap-1 mt-1 ml-1.5 px-2 py-0.5 text-[10px] sm:text-xs font-medium bg-blue-50 text-blue-600 rounded-full"
                >
                  <Fingerprint className="w-2.5 h-2.5" />
                  Biometric enabled
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <button
            onClick={openProfile}
            data-testid="edit-profile-btn"
            className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center">
                <User className="w-4 h-4 text-green-700" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-slate-900">Edit Profile</p>
                <p className="text-xs text-slate-500">Name, email, phone number</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </button>

          {/* Appearance / Theme */}
          <div className="w-full flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-violet-50 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0">
                {themeMode === 'light' && <Sun className="w-4 h-4 text-violet-600 dark:text-violet-400" />}
                {themeMode === 'dark'  && <Moon className="w-4 h-4 text-violet-600 dark:text-violet-400" />}
                {themeMode === 'auto'  && <Monitor className="w-4 h-4 text-violet-600 dark:text-violet-400" />}
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Appearance</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {themeMode === 'auto' ? 'Automatic (time-based)' : themeMode === 'dark' ? 'Dark mode' : 'Light mode'}
                </p>
              </div>
            </div>
            {/* Segmented picker */}
            <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden flex-shrink-0">
              {themeOptions.map(({ mode, icon, label }) => (
                <button
                  key={mode}
                  onClick={() => setThemeMode(mode)}
                  data-testid={`theme-option-${mode}`}
                  className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    themeMode === mode
                      ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  {icon}
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            data-testid="help-docs-btn"
            onClick={() => navigate('/help')}
            className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                <FileText className="w-4 h-4 text-slate-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-slate-900">Help & Docs</p>
                <p className="text-xs text-slate-500">Usage guide, permissions, FAQ</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </button>

          {biometricSupported && (
            <button
              onClick={() => biometricModal.open()}
              data-testid="biometric-settings-btn"
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center">
                  <Fingerprint className="w-4 h-4 text-blue-600" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-slate-900">Biometric Login</p>
                  <p className="text-xs text-slate-500">Fingerprint / Face ID for quick sign-in</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </button>
          )}

          <button
            onClick={handleTestPush}
            disabled={testingPush}
            data-testid="test-push-btn"
            className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors border-b border-slate-100"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center">
                <Bell className="w-4 h-4 text-green-600" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-slate-900">Test Push Notification</p>
                <p className="text-xs text-slate-500">{testingPush ? 'Sending…' : 'Send a test notification to this device'}</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </button>

          <button
            onClick={handleSignOut}
            data-testid="sign-out-btn"
            className="w-full flex items-center justify-between px-5 py-4 hover:bg-rose-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-rose-50 flex items-center justify-center">
                <LogOut className="w-4 h-4 text-rose-600" />
              </div>
              <p className="text-sm font-medium text-rose-600">Sign Out</p>
            </div>
          </button>
        </div>

        {/* Edit Profile Dialog */}
        <Dialog open={isProfileOpen} onOpenChange={profileModal.onOpenChange}>
          <DialogContent className="bg-white mx-4 sm:mx-auto max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Profile</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleProfileSave} className="space-y-4">
              <div>
                <Label htmlFor="profile-name">Name</Label>
                <Input
                  id="profile-name"
                  data-testid="profile-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="profile-email">Email</Label>
                <Input
                  id="profile-email"
                  type="email"
                  data-testid="profile-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="profile-phone">Phone Number</Label>
                <Input
                  id="profile-phone"
                  type="tel"
                  data-testid="profile-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Enter phone number"
                />
              </div>
              <Button type="submit" data-testid="save-profile-btn" className="w-full" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        {/* Biometric Login Dialog */}
        <Dialog open={isBiometricOpen} onOpenChange={biometricModal.onOpenChange}>
          <DialogContent className="bg-white mx-4 sm:mx-auto max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Fingerprint className="w-5 h-5 text-blue-600" /> Biometric Login
              </DialogTitle>
            </DialogHeader>

            <p className="text-sm text-slate-500 -mt-1">
              Use your fingerprint or Face ID to sign in quickly on trusted devices.
            </p>

            {/* Registered devices */}
            {credentials.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Registered Devices</p>
                {credentials.map((c) => (
                  <div key={c.cred_id} data-testid="biometric-credential-item"
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50 border border-slate-100"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-800">{c.device_name}</p>
                      <p className="text-xs text-slate-400">
                        Added {c.created_at ? new Date(c.created_at).toLocaleDateString() : '–'}
                        {c.last_used_at && ` · Last used ${new Date(c.last_used_at).toLocaleDateString()}`}
                      </p>
                    </div>
                    <DeleteButton
                      onConfirm={() => handleRemoveCredential(c.cred_id, c.device_name)}
                      className="p-1.5 rounded-lg"
                      title="Remove passkey"
                      testId="remove-credential-btn"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Add new device */}
            <div className="space-y-3 pt-1">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Add This Device</p>
              <div>
                <Label htmlFor="device-name">Device name (optional)</Label>
                <Input
                  id="device-name"
                  data-testid="device-name-input"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder={detectDeviceName?.() || 'My Device'}
                />
              </div>
              <Button
                data-testid="enroll-biometric-btn"
                onClick={handleEnrollBiometric}
                disabled={enrolling}
                className="w-full gap-2"
              >
                {enrolling
                  ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <Plus className="w-4 h-4" />
                }
                {enrolling ? 'Setting up…' : 'Register Biometric'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
};

export default Settings;
